/**
 * Repositorio del Learning Bank.
 *
 * AJUSTE 1: `kind` obligatorio, `area` nullable. El repositorio nunca inventa
 * un área: si viene `null`, se guarda `null`.
 */

import { liveQuery, type Observable } from 'dexie';

import type { AreaOpcional } from '../domain/modelo/areas.js';
import {
  estaActivo,
  participaEnPuntuacion,
  type ElementoBanco,
  type EstadoElemento,
  type FuenteElemento,
  type Kind,
} from '../domain/modelo/banco.js';
import { nuevoId } from '../domain/modelo/ids.js';
import { PRIORIDAD_POR_DEFECTO, type Prioridad } from '../domain/modelo/prioridad.js';
import {
  metadatosBorrados,
  metadatosEditados,
  metadatosNuevos,
} from '../domain/modelo/sincronizacion.js';
import { db, escribir } from './db.js';

export interface DatosElementoNuevo {
  kind: Kind;
  /** `null` es una opción legítima: sin clasificar. */
  area: AreaOpcional;
  contextoOriginal: string;
  correccionTarget: string;
  notas?: string;
  fuente: FuenteElemento;
  prioridad?: Prioridad;
  etiquetas?: string[];
  /** Clave estable del origen, para que una siembra sea idempotente. */
  origenClave?: string;
}

/** Filtros de la pantalla Banco. Todos opcionales y combinables. */
export interface FiltrosBanco {
  estados?: EstadoElemento[];
  kinds?: Kind[];
  /** `'sin_area'` selecciona los que no tienen clasificación. */
  areas?: (AreaOpcional | 'sin_area')[];
  prioridades?: Prioridad[];
  fuentes?: FuenteElemento[];
  /** Búsqueda textual simple sobre contexto, target y notas. */
  texto?: string;
}

export const bancoRepo = {
  async listar(): Promise<ElementoBanco[]> {
    const todos = await db.banco.toArray();
    return todos.filter((e) => !e.deleted);
  },

  observarTodos(): Observable<ElementoBanco[]> {
    return liveQuery(async () => bancoRepo.listar());
  },

  async obtener(id: string): Promise<ElementoBanco | undefined> {
    const elemento = await db.banco.get(id);
    return elemento && !elemento.deleted ? elemento : undefined;
  },

  /** Los que alimentan el Día 4. Los dominados quedan fuera, sin borrarse. */
  async activos(): Promise<ElementoBanco[]> {
    return (await bancoRepo.listar()).filter(estaActivo);
  },

  /** Los que participan en la puntuación del Día 5: activos Y con área. */
  async puntuables(): Promise<ElementoBanco[]> {
    return (await bancoRepo.listar()).filter(participaEnPuntuacion);
  },

  async crear(datos: DatosElementoNuevo): Promise<ElementoBanco> {
    const elemento: ElementoBanco = {
      id: nuevoId(),
      kind: datos.kind,
      area: datos.area,
      contextoOriginal: datos.contextoOriginal,
      correccionTarget: datos.correccionTarget,
      notas: datos.notas,
      fuente: datos.fuente,
      prioridad: datos.prioridad ?? PRIORIDAD_POR_DEFECTO,
      estado: 'activo',
      vecesOcurrido: 1,
      creadoEn: new Date().toISOString(),
      etiquetas: datos.etiquetas ?? [],
      origenClave: datos.origenClave,
      ...metadatosNuevos(),
    };
    await escribir('crear el elemento', () => db.banco.add(elemento));
    return elemento;
  },

  async guardar(elemento: ElementoBanco): Promise<ElementoBanco> {
    const actualizado = { ...elemento, ...metadatosEditados(elemento) };
    await escribir('guardar el elemento', () => db.banco.put(actualizado));
    return actualizado;
  },

  async cambiarEstado(id: string, estado: EstadoElemento): Promise<void> {
    const elemento = await bancoRepo.obtener(id);
    if (!elemento) return;
    await bancoRepo.guardar({ ...elemento, estado });
  },

  /**
   * «Volvió a pasar».
   *
   * Es la UNICA forma de subir el contador (CA-5.8). No hay deteccion
   * automatica por similitud de texto: seria fragil, y un contador equivocado
   * contamina la puntuacion del Dia 5, que tiene que ser confiable.
   */
  async registrarOcurrencia(id: string): Promise<void> {
    const elemento = await bancoRepo.obtener(id);
    if (!elemento) return;
    await bancoRepo.guardar({
      ...elemento,
      vecesOcurrido: elemento.vecesOcurrido + 1,
      ultimaVezVistoEn: new Date().toISOString(),
    });
  },

  async borrar(id: string): Promise<void> {
    const elemento = await db.banco.get(id);
    if (!elemento) return;
    await escribir('borrar el elemento', () =>
      db.banco.put({ ...elemento, ...metadatosBorrados(elemento) }),
    );
  },

  /**
   * Siembra idempotente.
   *
   * Si ya existe un elemento con la misma `origenClave`, lo ACTUALIZA en vez de
   * crear otro. Es lo que permite volver a guardar el cierre del baseline sin
   * duplicar nada, y conserva la identidad del elemento si se editó después.
   */
  async sembrar(datos: DatosElementoNuevo & { origenClave: string }): Promise<ElementoBanco> {
    const existente = (await bancoRepo.listar()).find(
      (e) => e.origenClave === datos.origenClave,
    );
    if (!existente) return bancoRepo.crear(datos);

    return bancoRepo.guardar({
      ...existente,
      kind: datos.kind,
      area: datos.area,
      contextoOriginal: datos.contextoOriginal,
      correccionTarget: datos.correccionTarget,
      notas: datos.notas,
      prioridad: datos.prioridad ?? existente.prioridad,
    });
  },

  /** Marca que un elemento se practicó DE VERDAD, no solo que se seleccionó. */
  async registrarPractica(id: string): Promise<void> {
    const elemento = await bancoRepo.obtener(id);
    if (!elemento) return;
    await bancoRepo.guardar({ ...elemento, ultimaPracticaEn: new Date().toISOString() });
  },

  async pendientesDeSincronizar(): Promise<ElementoBanco[]> {
    return (await db.banco.toArray()).filter((e) => e.dirty);
  },
};

/**
 * Aplica los filtros de la pantalla Banco.
 *
 * Función pura sobre una lista ya cargada: a este volumen —decenas o pocos
 * cientos de elementos— filtrar en memoria es instantáneo y evita un índice
 * compuesto por cada combinación posible.
 */
export function filtrarBanco(
  elementos: ElementoBanco[],
  filtros: FiltrosBanco,
): ElementoBanco[] {
  const texto = filtros.texto?.trim().toLowerCase();

  return elementos.filter((e) => {
    if (filtros.estados?.length && !filtros.estados.includes(e.estado)) return false;
    if (filtros.kinds?.length && !filtros.kinds.includes(e.kind)) return false;
    if (filtros.prioridades?.length && !filtros.prioridades.includes(e.prioridad)) {
      return false;
    }
    if (filtros.fuentes?.length && !filtros.fuentes.includes(e.fuente)) return false;

    if (filtros.areas?.length) {
      const coincide = filtros.areas.some((a) =>
        a === 'sin_area' ? e.area === null : e.area === a,
      );
      if (!coincide) return false;
    }

    if (texto) {
      const enTexto = [e.contextoOriginal, e.correccionTarget, e.notas ?? '']
        .join(' ')
        .toLowerCase();
      if (!enTexto.includes(texto)) return false;
    }

    return true;
  });
}
